package com.example.histoire.web

import androidx.compose.runtime.Composable
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.StyleScope
import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.css.backgroundColor
import org.jetbrains.compose.web.css.color
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.flexWrap
import org.jetbrains.compose.web.css.fontSize
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.margin
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.attributes.InputType

@Composable
fun FamilySettingsMenu(
    open: Boolean,
    onToggleOpen: () -> Unit,
    onOpenChildProfiles: () -> Unit,
    biometricUnlockEnabled: Boolean,
    onToggleBiometricUnlock: (Boolean) -> Unit,
    onResetBiometricUnlock: () -> Unit,
    onContactSupport: () -> Unit,
    onLogout: () -> Unit,
    readerPrefs: ReaderUiPrefs,
    onReaderPrefsChange: (ReaderUiPrefs) -> Unit,
    detectedImageLuminance: Double?,
) {
    fun update(block: (ReaderUiPrefs) -> ReaderUiPrefs) {
        onReaderPrefsChange(block(readerPrefs))
    }

    Div({ style { settingsMenuRoot() } }) {
        Button(attrs = {
            attr("type", "button")
            onClick { onToggleOpen() }
            style { settingsMenuTrigger() }
        }) {
            Text(if (open) "Paramètres ▲" else "Paramètres ▼")
        }
        if (open) {
            Div({ style { settingsMenuDropdown() } }) {
                P({ style { settingsSectionTitle() } }) { Text("Texte et lisibilité") }

                P({ style { settingsHint() } }) { Text("Style du texte") }
                Div({ style { modeRow() } }) {
                    listOf(
                        "auto" to "Auto",
                        "light" to "Clair",
                        "dark" to "Foncé",
                        "custom" to "Perso",
                    ).forEach { (mode, label) ->
                        Button(attrs = {
                            attr("type", "button")
                            onClick { update { it.copy(textMode = mode) } }
                            style { modeChip(readerPrefs.textMode == mode) }
                        }) { Text(label) }
                    }
                }

                if (readerPrefs.textMode == "custom") {
                    Div({ style { settingsRow() } }) {
                        P({ style { settingsHint() } }) { Text("Couleur du texte") }
                        Input(type = InputType.Color, attrs = {
                            value(normalizeHexColor(readerPrefs.textColorHex))
                            onInput { ev ->
                                update { it.copy(textColorHex = normalizeHexColor(ev.value)) }
                            }
                            style { colorPickerInput() }
                        })
                    }
                } else if (readerPrefs.textMode == "auto" && detectedImageLuminance != null) {
                    P({ style { settingsHint() } }) {
                        Text(
                            "Photo analysée : " +
                                if (detectedImageLuminance >= 0.55) {
                                    "plutôt claire → texte foncé"
                                } else {
                                    "plutôt sombre → texte clair"
                                },
                        )
                    }
                }

                P({ style { settingsHint() } }) { Text("Fond sous le texte") }
                Div({ style { settingsRow() } }) {
                    Button(attrs = {
                        attr("type", "button")
                        onClick { update { it.copy(backdropEnabled = !it.backdropEnabled) } }
                        style { modeChip(readerPrefs.backdropEnabled) }
                    }) {
                        Text(if (readerPrefs.backdropEnabled) "Activé" else "Désactivé")
                    }
                }

                if (readerPrefs.backdropEnabled) {
                    Div({ style { settingsRow() } }) {
                        P({ style { settingsHint() } }) { Text("Couleur du fond") }
                        Input(type = InputType.Color, attrs = {
                            value(normalizeHexColor(readerPrefs.backdropColorHex))
                            onInput { ev ->
                                update { it.copy(backdropColorHex = normalizeHexColor(ev.value)) }
                            }
                            style { colorPickerInput() }
                        })
                    }
                    Div({ style { settingsRow() } }) {
                        P({ style { settingsHint() } }) {
                            Text("Opacité : ${readerPrefs.backdropOpacityPercent} %")
                        }
                        Input(type = InputType.Range, attrs = {
                            attr("min", "15")
                            attr("max", "85")
                            value(readerPrefs.backdropOpacityPercent.toString())
                            onInput { ev ->
                                val v =
                                    "${ev.value}".toDoubleOrNull()?.toInt()?.coerceIn(0, 100)
                                        ?: readerPrefs.backdropOpacityPercent
                                update { it.copy(backdropOpacityPercent = v) }
                            }
                            style { opacityRange() }
                        })
                    }
                }

                P({ style { settingsSectionTitle() } }) { Text("Compte") }
                Button(attrs = {
                    attr("type", "button")
                    onClick {
                        onOpenChildProfiles()
                    }
                    style { settingsMenuItem() }
                }) { Text("👨‍👩‍👧‍👦 Ma famille") }
                Button(attrs = {
                    attr("type", "button")
                    onClick { onContactSupport() }
                    style { settingsMenuItem() }
                }) { Text("✉️ Nous contacter") }
                Div({ style { settingsRow() } }) {
                    Div({ style { biometricSwitchCard() } }) {
                        P({ style { settingsMenuItemLabel() } }) { Text("🔐 Déverrouillage biométrique") }
                        Button(attrs = {
                            attr("type", "button")
                            attr("role", "switch")
                            attr("aria-checked", if (biometricUnlockEnabled) "true" else "false")
                            attr(
                                "aria-label",
                                if (biometricUnlockEnabled) {
                                    "Désactiver le déverrouillage biométrique"
                                } else {
                                    "Activer le déverrouillage biométrique"
                                },
                            )
                            onClick { onToggleBiometricUnlock(!biometricUnlockEnabled) }
                            style { biometricSwitchTrack(biometricUnlockEnabled) }
                        }) {
                            Div({ style { biometricSwitchThumb(biometricUnlockEnabled) } })
                        }
                    }
                }
                Button(attrs = {
                    attr("type", "button")
                    onClick { onResetBiometricUnlock() }
                    style { settingsMenuItem() }
                }) { Text("🗑️ Réinitialiser la biométrie") }
                Button(attrs = {
                    attr("type", "button")
                    onClick { onLogout() }
                    style { settingsMenuItemDanger() }
                }) { Text("Se déconnecter") }
            }
        }
    }
}

private fun StyleScope.settingsSectionTitle() {
    width(100.percent)
    color(Color("#94A3B8"))
    fontSize(11.px)
    margin(8.px, 2.px, 2.px, 2.px)
    property("font-weight", "800")
    property("letter-spacing", "0.06em")
    property("text-transform", "uppercase")
}

private fun StyleScope.settingsHint() {
    width(100.percent)
    color(Color("#CBD5E1"))
    fontSize(12.px)
    margin(4.px, 2.px, 2.px, 2.px)
}

private fun StyleScope.modeRow() {
    display(DisplayStyle.Flex)
    flexWrap(FlexWrap.Wrap)
    gap(6.px)
    width(100.percent)
}

private fun StyleScope.modeChip(active: Boolean) {
    padding(10.px, 12.px)
    color(Color("#F8FAFC"))
    backgroundColor(
        if (active) Color("#334155") else Color("rgba(255,255,255,0.08)"),
    )
    property(
        "border",
        if (active) "1px solid #94A3B8" else "1px solid rgba(255,255,255,0.16)",
    )
    property("border-radius", "999px")
    property("font-weight", "700")
    property("font-size", "14px")
    property("min-height", "40px")
    property("cursor", "pointer")
}

private fun StyleScope.settingsRow() {
    display(DisplayStyle.Flex)
    flexDirection(FlexDirection.Column)
    gap(6.px)
    width(100.percent)
    margin(4.px, 0.px)
}

private fun StyleScope.colorPickerInput() {
    width(100.percent)
    property("max-width", "160px")
    height(36.px)
    property("border", "none")
    property("border-radius", "10px")
    property("cursor", "pointer")
    padding(0.px)
}

private fun StyleScope.opacityRange() {
    width(100.percent)
    margin(2.px, 0.px)
}

private fun StyleScope.settingsMenuRoot() {
    property("position", "relative")
    display(DisplayStyle.Flex)
    flexDirection(FlexDirection.Column)
    alignItems(AlignItems.FlexEnd)
    width(100.percent)
}

private fun StyleScope.settingsMenuTrigger() {
    padding(12.px, 14.px)
    color(Color.white)
    backgroundColor(Color("rgba(30, 41, 59, 0.95)"))
    property("border", "1px solid rgba(148, 163, 184, 0.45)")
    property("border-radius", "12px")
    property("font-weight", "700")
    property("font-size", "16px")
    property("min-height", "44px")
    property("cursor", "pointer")
}

private fun StyleScope.settingsMenuDropdown() {
    property("position", "absolute")
    property("top", "100%")
    property("right", "0")
    property("margin-top", "6px")
    property("min-width", "0")
    width(100.percent)
    property("max-width", "min(92vw, 360px)")
    property("max-height", "min(70vh, 520px)")
    property("overflow-y", "auto")
    property("z-index", "50")
    display(DisplayStyle.Flex)
    flexDirection(FlexDirection.Column)
    gap(4.px)
    padding(10.px)
    backgroundColor(Color("rgba(15, 23, 42, 0.98)"))
    property("border", "1px solid rgba(148, 163, 184, 0.4)")
    property("border-radius", "14px")
    property("box-shadow", "0 12px 32px rgba(0,0,0,0.35)")
}

private fun StyleScope.settingsMenuItem() {
    width(100.percent)
    padding(14.px)
    color(Color("#F1F5F9"))
    backgroundColor(Color("rgba(255,255,255,0.06)"))
    property("border", "1px solid rgba(255,255,255,0.12)")
    property("border-radius", "10px")
    property("font-weight", "600")
    property("font-size", "16px")
    property("min-height", "46px")
    property("text-align", "left")
    property("cursor", "pointer")
}

private fun StyleScope.settingsMenuItemDanger() {
    width(100.percent)
    padding(14.px)
    color(Color("#FECACA"))
    backgroundColor(Color("rgba(127, 29, 29, 0.35)"))
    property("border", "1px solid rgba(248, 113, 113, 0.45)")
    property("border-radius", "10px")
    property("font-weight", "600")
    property("font-size", "16px")
    property("min-height", "46px")
    property("text-align", "left")
    property("cursor", "pointer")
}

private fun StyleScope.settingsMenuItemLabel() {
    margin(0.px)
    color(Color("#F1F5F9"))
    fontSize(16.px)
    property("font-weight", "600")
}

private fun StyleScope.biometricSwitchTrack(enabled: Boolean) {
    width(58.px)
    height(32.px)
    padding(2.px)
    backgroundColor(if (enabled) Color("#22C55E") else Color("#64748B"))
    property("border", if (enabled) "1px solid #16A34A" else "1px solid #475569")
    property("border-radius", "999px")
    property("cursor", "pointer")
    property("display", "inline-flex")
    property("align-items", "center")
    property("justify-content", if (enabled) "flex-end" else "flex-start")
    property("transition", "background-color 160ms ease, border-color 160ms ease")
}

private fun StyleScope.biometricSwitchThumb(enabled: Boolean) {
    width(26.px)
    height(26.px)
    backgroundColor(Color.white)
    property("border-radius", "50%")
    property("box-shadow", "0 1px 3px rgba(0,0,0,0.35)")
    property("transform", if (enabled) "translateX(0)" else "translateX(0)")
}

private fun StyleScope.biometricSwitchCard() {
    width(100.percent)
    display(DisplayStyle.Flex)
    property("align-items", "center")
    property("justify-content", "space-between")
    gap(10.px)
    padding(12.px)
    backgroundColor(Color("rgba(255,255,255,0.06)"))
    property("border", "1px solid rgba(255,255,255,0.12)")
    property("border-radius", "10px")
}
